import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Megaphone, Calendar, Clock, Plus, FileText, ExternalLink,
  CheckCircle, ChevronRight, BookOpen
} from "lucide-react";
import DOMPurify from "dompurify";

export function NewsEventsZone({
  user,
  newsFeed,
  events,
  expandedFeedId,
  setExpandedFeedId,
  setShowCreateBulletin,
  setShowCreateEvent,
  handleRegisterEvent,
  stripHtml
}) {
  return (
    <div className="space-y-6">
      {/* How-to Instructions Card */}
      <Card className="bg-gradient-to-r from-pink-500/10 to-neutral-900/50 border-pink-500/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-pink-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-5 h-5 text-pink-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-white mb-2">How to Stay Connected with News & Events</h3>
              <ul className="text-sm text-neutral-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-pink-400 font-bold">1.</span>
                  <span><strong>Check announcements</strong> - Important updates from consultants and community leaders appear here</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pink-400 font-bold">2.</span>
                  <span><strong>Read newsletters</strong> - Get curated business insights and success stories from the community</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pink-400 font-bold">3.</span>
                  <span><strong>RSVP to events</strong> - View upcoming webinars, workshops, and networking sessions on the right</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pink-400 font-bold">4.</span>
                  <span><strong>Join live sessions</strong> - Click the meeting link when events are happening to participate</span>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* News Feed */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-[#39FF14]" />
              Community Updates
            </h2>
            {user?.role === "consultant" && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => setShowCreateBulletin(true)}
                  className="bg-[#39FF14] text-black hover:bg-[#32e612]"
                  data-testid="create-bulletin-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Post Update
                </Button>
              </div>
            )}
          </div>

          {newsFeed.length > 0 ? newsFeed.map((item, idx) => (
            <Card key={idx} className="bg-neutral-800/50 border-neutral-700 hover:border-neutral-600 transition-colors cursor-pointer" onClick={() => setExpandedFeedId(expandedFeedId === idx ? null : idx)}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    item.feed_type === "bulletin" ? "bg-blue-500/10" :
                    item.feed_type === "newsletter" ? "bg-purple-500/10" :
                    "bg-emerald-500/10"
                  }`}>
                    {item.feed_type === "bulletin" && <Megaphone className="w-5 h-5 text-blue-400" />}
                    {item.feed_type === "newsletter" && <FileText className="w-5 h-5 text-purple-400" />}
                    {item.feed_type === "event" && <Calendar className="w-5 h-5 text-emerald-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className={`text-xs ${
                        item.feed_type === "bulletin" ? "border-blue-500/30 text-blue-400" :
                        item.feed_type === "newsletter" ? "border-purple-500/30 text-purple-400" :
                        "border-emerald-500/30 text-emerald-400"
                      }`}>
                        {item.feed_type === "bulletin" ? "Announcement" :
                         item.feed_type === "newsletter" ? "Newsletter" : "Event"}
                      </Badge>
                      <span className="text-xs text-neutral-500">
                        {item.created_at ? new Date(item.created_at).toLocaleDateString() : ""}
                      </span>
                    </div>
                    <h3 className="font-medium text-white">{item.title || item.subject}</h3>
                    {expandedFeedId === idx ? (
                      <div className="mt-2">
                        <div className="text-sm text-neutral-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.content || item.description) }} />
                        {item.flyer_image && (
                          <img src={item.flyer_image} alt="Flyer" className="mt-3 w-full max-h-64 object-cover rounded-lg border border-neutral-700" />
                        )}
                        {item.feed_type === "event" && item.workshop_id && (
                          <Button size="sm" className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={(e) => { e.stopPropagation(); window.open(`/event/${item.workshop_id}`, "_blank"); }}>
                            <ExternalLink className="w-3 h-3 mr-1" /> View Full Event Page
                          </Button>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-neutral-400 mt-1 line-clamp-2">
                        {stripHtml(item.content || item.description)}
                      </p>
                    )}
                    {item.feed_type === "event" && (
                      <div className="flex items-center gap-4 mt-3 text-sm text-neutral-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {item.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {item.time}
                        </span>
                      </div>
                    )}
                    {expandedFeedId !== idx && (
                      <p className="text-xs text-neutral-600 mt-2 flex items-center gap-1"><ChevronRight className="w-3 h-3" /> Click to read more</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )) : (
            <Card className="bg-neutral-800/50 border-neutral-700">
              <CardContent className="text-center py-12">
                <Megaphone className="w-12 h-12 mx-auto mb-3 text-neutral-600" />
                <p className="text-neutral-400">No updates yet</p>
                {user?.role === "consultant" && (
                  <p className="text-sm text-neutral-500 mt-1">Post the first announcement!</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Upcoming Events Sidebar */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#39FF14]" />
              Upcoming Events
            </h2>
            {user?.role === "consultant" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowCreateEvent(true)}
                className="border-neutral-600"
                data-testid="create-event-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            )}
          </div>

          {events.length > 0 ? events.map((event, idx) => (
            <Card key={idx} className="bg-neutral-800/50 border-neutral-700">
              <CardContent className="pt-4 pb-4">
                <div className="space-y-3">
                  <div>
                    <h3 className="font-medium text-white">{event.title}</h3>
                    <p className="text-sm text-neutral-400 line-clamp-2 mt-1">{event.description}</p>
                    {event.flyer_image && (
                      <img src={event.flyer_image} alt="Event flyer" className="mt-2 w-full max-h-32 object-cover rounded-lg border border-neutral-700" />
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-neutral-400">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {event.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {event.time}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-neutral-500">
                      {event.spots_left > 0 ? `${event.spots_left} spots left` : "Full"}
                    </span>
                    {event.is_registered ? (
                      <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Registered
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleRegisterEvent(event.workshop_id)}
                        disabled={event.spots_left <= 0}
                        className="bg-[#39FF14] text-black hover:bg-[#32e612]"
                        data-testid={`register-event-${event.workshop_id}`}
                      >
                        Register
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )) : (
            <Card className="bg-neutral-800/50 border-neutral-700">
              <CardContent className="text-center py-8">
                <Calendar className="w-10 h-10 mx-auto mb-2 text-neutral-600" />
                <p className="text-sm text-neutral-400">No upcoming events</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
